import React, {useEffect, useState} from 'react';
import {Distribution, DistributionType, DropdownItemType, AdditionalDistributionLayer} from "../../model/EstateFlowchartTypes";
import {REMAINDER_BENEFICIARIES_CONSTANTS as Labels} from "../../constants/LabelConstants";
import FormPage from "../../components/FormPage/FormPage";
import DistributionList from "../../components/FormContent/DistributionList/DistributionList";
import FormPageSection from "../../components/FormPageSection/FormPageSection";
import {Button} from "../../../components";
import AdditionalDistributionLayerEventTrigger from "./AdditionalDistributionLayerEventTrigger";
import {familyMembersSelector} from "../../slices/EstateFlowChartSlice";
import {useAppSelector} from "../../../store/hooks";
import {getSectionId} from "../../components/FormNav/FormNavUtils";
import "./_remainder-beneficiaries-content.scss";
import { selectReleaseToggles } from 'src/ReleaseToggles/releaseTogglesSlice';

type RemainderBeneficiariesContentProps = {
    remainderDistributions: Distribution[];
    additionalDistributions?: AdditionalDistributionLayer;
    updateEstateItem: (distributions: Distribution[]) => void;
    updateAdditionalDistributions: (additionalDistributions?: AdditionalDistributionLayer) => void;
    saveForm: () => Promise<boolean>;
    pageIndex: number;
};

const createEmptyAdditionalDistributionLayer = (): AdditionalDistributionLayer => ({
    triggerEventType: "",
    triggerMemberId: "",
    triggerAge: null,
    triggerEventText: ""
});

const RemainderBeneficiariesContent: React.FC<RemainderBeneficiariesContentProps> =
    ({
        remainderDistributions,
        additionalDistributions,
        updateEstateItem,
        updateAdditionalDistributions,
        saveForm,
        pageIndex
    }: RemainderBeneficiariesContentProps): JSX.Element => {

    const releaseToggles = useAppSelector(selectReleaseToggles);
        const [distributionList, setDistributionList] = useState<Distribution[]>(remainderDistributions);
        const [additionalDistribution, setAdditionalDistribution] = useState<AdditionalDistributionLayer | undefined>(
            additionalDistributions
        );
        const familyMembers = useAppSelector(familyMembersSelector);
        const hasInTrustDistribution = distributionList.some(
            (distribution) => distribution.distributionType === DistributionType.IN_TRUST
        );
        const hasAdditionalLayer = !!additionalDistribution;

        useEffect(() => {
            setDistributionList(remainderDistributions);
        }, [remainderDistributions]);

        useEffect(() => {
            setAdditionalDistribution(additionalDistributions);
        }, [additionalDistributions]);

        const resetEventTrigger = () => {
            setAdditionalDistribution(undefined);
            updateAdditionalDistributions(undefined);
        };

        const handleAddLayer = () => {
            const nextAdditionalDistribution = createEmptyAdditionalDistributionLayer();
            setAdditionalDistribution(nextAdditionalDistribution);
            updateAdditionalDistributions(nextAdditionalDistribution);
        };

        const handleAdditionalLayerChange = (layer: AdditionalDistributionLayer) => {
            setAdditionalDistribution(layer);
            updateAdditionalDistributions(layer);
        };

        const dropdownOptions: DropdownItemType[] = [
            {name: "In Trust", type: DistributionType.IN_TRUST},
            {name: "Charity", type: DistributionType.CHARITY},
            {name: "Outright", type: DistributionType.OUTRIGHT},
        ];

        return (
            <FormPage title={Labels.TITLE_LABEL_TEXT} subText={Labels.TITLE_SUB_TEXT} pageIndex={pageIndex}>
                <DistributionList
                    distributions={distributionList}
                    updateEstateItem={(newDistributions) => {
                        setDistributionList(newDistributions);
                        updateEstateItem(newDistributions);
                    }}
                    saveForm={saveForm}
                    pageIndex={pageIndex}
                    addDistributionOptions={dropdownOptions}/>
                {
                    releaseToggles?.enableAdditionalDistributionLayer && (
                <FormPageSection id={getSectionId(pageIndex, distributionList.length)}>
                    <div className="additional-distribution-layer">
                        <div className="additional-distribution-layer-divider"/>
                        <div className="additional-distribution-layer-header">
                            <span>{Labels.ADDITIONAL_LAYER_SECTION_TITLE}</span>
                            {!hasAdditionalLayer && (
                                <Button
                                    onClick={handleAddLayer}
                                    icon="left"
                                    iconName="add"
                                    kind="borderless"
                                    size="medium"
                                    disabled={!hasInTrustDistribution}
                                >
                                    {Labels.ADDITIONAL_LAYER_BUTTON_TEXT}
                                </Button>
                            )}
                        </div>
                        <p className="additional-distribution-layer-helper-text">
                            {Labels.ADDITIONAL_LAYER_HELPER_TEXT_PREFIX}
                            <strong>In Trust</strong>
                            {Labels.ADDITIONAL_LAYER_HELPER_TEXT_SUFFIX}
                        </p>
                        {hasAdditionalLayer && additionalDistribution && (
                            <AdditionalDistributionLayerEventTrigger
                                layer={additionalDistribution}
                                familyMembers={familyMembers}
                                onChange={handleAdditionalLayerChange}
                                onReset={resetEventTrigger}
                            />
                        )}
                    </div>
                </FormPageSection>
                    )
                }

            </FormPage>
        );
    }

export default RemainderBeneficiariesContent;